'use strict'

import React, { useCallback, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useHistory } from 'react-router-dom'
import RoutingBreadcrumbs from './RoutingBreadcrumbs'
import {
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { DFBadge, DFSearchInput } from '../df'

const STATUS_CONFIG = {
  draft:     { stripe: '#c66a45', dot: '#c66a45', label: 'Draft' },
  published: { stripe: '#1f5e3a', dot: '#1f5e3a', label: 'Published' },
  archived:  { stripe: '#97a094', dot: '#97a094', label: 'Archived' }
}

const fmtDate = (v) => {
  if (!v) return '—'
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return '—'
  const mo = d.toLocaleString('en-US', { month: 'short' })
  return `${mo} ${d.getDate()}`
}

function ScenarioCard ({ scenario, onOpen, onPublish, isPublishing }) {
  const [hov, setHov] = useState(false)
  const st = STATUS_CONFIG[scenario.status] || STATUS_CONFIG.archived
  const isDraft = scenario.status === 'draft'

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: 'var(--df-surface)',
        borderRadius: 12,
        border: `1.5px solid ${hov ? (isDraft ? 'var(--df-accent)' : 'var(--df-line-2)') : 'var(--df-line)'}`,
        boxShadow: hov ? '0 4px 16px rgba(28,36,25,0.09)' : '0 1px 3px rgba(28,36,25,0.05)',
        overflow: 'hidden',
        transition: 'all 0.18s',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div style={{ height: 3, background: st.stripe, flexShrink: 0 }} />
      <div style={{ padding: '14px 16px 12px', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--df-ink)', lineHeight: 1.3, marginBottom: 3 }}>
              {scenario.name}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <DFBadge status={scenario.status} label={st.label} />
              {scenario.isStaleAtCreation && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  fontSize: 11, fontWeight: 600, color: 'var(--df-accent)',
                  background: 'var(--df-warn-bg)', border: '1px solid var(--df-warn)',
                  padding: '2px 7px', borderRadius: 99
                }}>
                  Stale sync
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex', gap: 16, padding: '10px 0',
          borderTop: '1px solid var(--df-line)', borderBottom: '1px solid var(--df-line)'
        }}>
          <MetricCell label="Overrides" value={scenario.assignmentCount || 0} />
          <MetricCell label="Created" value={fmtDate(scenario.createdAt)} />
          {scenario.publishedAt && <MetricCell label="Published" value={fmtDate(scenario.publishedAt)} />}
          <MetricCell label="Horizon" value={`${scenario.horizonDays || 30}d`} />
        </div>

        <div style={{ display: 'flex', gap: 6, marginTop: 12, alignItems: 'center' }}>
          <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 12px' }} onClick={onOpen}>
            Open Workbench
          </button>
          <button className="btn btn-ghost" style={{ fontSize: 12, padding: '6px 10px' }} onClick={() => onOpen('compare')}>
            Compare
          </button>
          <button className="btn btn-ghost" style={{ fontSize: 12, padding: '6px 10px' }} onClick={() => onOpen('map')}>
            Map
          </button>
          {isDraft && (
            <button
              className="btn btn-accent"
              style={{ fontSize: 12, padding: '6px 12px', marginLeft: 'auto' }}
              onClick={onPublish}
              disabled={isPublishing}
            >
              {isPublishing ? 'Publishing…' : 'Publish'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function MetricCell ({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--df-ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </span>
      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--df-ink)' }}>{value}</span>
    </div>
  )
}

function RoutingScenarioDashboard ({ url }) {
  const history = useHistory()

  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [publishingById, setPublishingById] = useState({})
  const [scenarios, setScenarios] = useState([])
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' })

  const counts = useMemo(() => ({
    total: scenarios.length,
    draft: scenarios.filter(s => s.status === 'draft').length,
    published: scenarios.filter(s => s.status === 'published').length,
    archived: scenarios.filter(s => s.status === 'archived').length
  }), [scenarios])

  const filtered = useMemo(() => {
    let s = scenarios
    if (filter !== 'all') s = s.filter(x => x.status === filter)
    if (search.trim()) s = s.filter(x => (x.name || '').toLowerCase().includes(search.toLowerCase()))
    return s
  }, [scenarios, filter, search])

  const downloadChangePackage = useCallback((changePackage) => {
    if (!changePackage?.csv) return false
    const blob = new Blob([changePackage.csv], { type: changePackage.contentType || 'text/csv;charset=utf-8;' })
    const objectUrl = window.URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = changePackage.fileName || `territory_publish_changes_${Date.now()}.csv`
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    window.URL.revokeObjectURL(objectUrl)
    return true
  }, [])

  const fetchScenarios = useCallback(async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${url}/api/hmi/territory/scenarios`)
      setScenarios(response.data?.scenarios || [])
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || err.message || 'Failed to load scenarios', severity: 'error' })
    } finally {
      setLoading(false)
    }
  }, [url])

  React.useEffect(() => { fetchScenarios() }, [fetchScenarios])

  const createScenario = async (payload) => {
    setCreating(true)
    try {
      const response = await axios.post(`${url}/api/hmi/territory/scenarios`, payload)
      const created = response.data?.scenario
      await fetchScenarios()
      setCreateModalOpen(false)
      setSnackbar({
        open: true,
        message: created?.isStaleAtCreation ? 'Draft scenario created (stale data acknowledged)' : 'Draft scenario created',
        severity: 'success'
      })
      if (created?._id) history.push(`/routing/territory?scenarioId=${created._id}`)
    } catch (err) {
      const apiError = err.response?.data
      if (apiError?.requiresFreshnessDecision) {
        setSnackbar({ open: true, message: 'Scenario data is stale. Toggle "Allow stale data" to proceed.', severity: 'warning' })
      } else {
        setSnackbar({ open: true, message: apiError?.message || err.message || 'Failed to create scenario', severity: 'error' })
      }
    } finally {
      setCreating(false)
    }
  }

  const publishScenario = async (scenario) => {
    if (!scenario?._id || scenario.status !== 'draft') return
    const confirmed = window.confirm(`Publish scenario "${scenario.name}"? This will archive the current published scenario and write changes to live routing data.`)
    if (!confirmed) return

    setPublishingById(prev => ({ ...prev, [scenario._id]: true }))
    try {
      const response = await axios.post(`${url}/api/hmi/territory/scenarios/${scenario._id}/publish`)
      const changePackage = response.data?.changePackage
      const csvDownloaded = downloadChangePackage(changePackage)
      await fetchScenarios()
      setSnackbar({
        open: true,
        message: csvDownloaded ? `Published and downloaded CSV (${changePackage?.rowCount || 0} moved)` : 'Scenario published',
        severity: 'success'
      })
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || err.message || 'Failed to publish', severity: 'error' })
    } finally {
      setPublishingById(prev => ({ ...prev, [scenario._id]: false }))
    }
  }

  const openScenario = (scenario, tab) => {
    const base = `/routing/territory?scenarioId=${scenario._id}`
    if (tab === 'compare') history.push(`${base}#scenario-compare`)
    else if (tab === 'map') history.push(`/routing/territory/map?scenarioId=${scenario._id}`)
    else history.push(base)
  }

  const STATS = [
    { label: 'Total', value: counts.total, c: 'var(--df-ink)', bg: 'var(--df-bg)', key: 'all' },
    { label: 'Draft', value: counts.draft, c: '#a4502f', bg: 'var(--df-warn-bg)', key: 'draft' },
    { label: 'Published', value: counts.published, c: '#164a2c', bg: 'var(--df-success-bg)', key: 'published' },
    { label: 'Archived', value: counts.archived, c: 'var(--df-ink-3)', bg: 'var(--df-bg)', key: 'archived' }
  ]

  return (
    <div style={{
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--df-bg)',
      animation: 'df-scaleIn .25s ease'
    }}>
      <div style={{ padding: '24px 32px 0', maxWidth: 900 }}>
        <RoutingBreadcrumbs items={[{ label: 'Territory Scenarios' }]} />
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--df-ink)', letterSpacing: '-0.02em', lineHeight: 1.2, margin: 0 }}>
              Territory Scenarios
            </h1>
            <p style={{ fontSize: 13, color: 'var(--df-ink-3)', marginTop: 4, maxWidth: 520 }}>
              Sandbox drafts let you experiment with routing changes before they go live. Create a draft, review in the workbench, then publish when ready.
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => setCreateModalOpen(true)}>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>
            New Scenario
          </button>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          {STATS.map(stat => (
            <button
              key={stat.key}
              onClick={() => setFilter(stat.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
                borderRadius: 8, border: `1.5px solid ${filter === stat.key ? stat.c + '44' : 'var(--df-line)'}`,
                background: filter === stat.key ? stat.bg : 'var(--df-surface)',
                cursor: 'pointer', transition: 'all 0.12s', fontFamily: 'inherit'
              }}
            >
              <span style={{ fontSize: 18, fontWeight: 800, color: stat.c, lineHeight: 1 }}>{stat.value}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--df-ink-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</span>
            </button>
          ))}
          <DFSearchInput value={search} onChange={e => setSearch(e.target.value)} placeholder="Search scenarios…" />
          <button className="btn btn-ghost" onClick={fetchScenarios} disabled={loading} style={{ marginLeft: 'auto' }}>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4v5h5M20 20v-5h-5"/><path d="M20.49 9A9 9 0 005.64 5.64L4 4m16.36 16.36A9 9 0 019 20.49"/></svg>
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <CircularProgress size={28} style={{ color: 'var(--df-primary)' }} />
        </div>
      ) : (
        <div style={{
          padding: '20px 32px 32px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
          gap: 14,
          maxWidth: 900
        }}>
          {filtered.map(s => (
            <ScenarioCard
              key={s._id}
              scenario={s}
              onOpen={(tab) => openScenario(s, tab)}
              onPublish={() => publishScenario(s)}
              isPublishing={!!publishingById[s._id]}
            />
          ))}

          {filter === 'all' && (
            <button
              onClick={() => setCreateModalOpen(true)}
              style={{
                border: '1.5px dashed var(--df-line-2)', borderRadius: 12, background: 'transparent',
                cursor: 'pointer', padding: '28px 20px', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 10, transition: 'all 0.15s',
                fontFamily: 'inherit', minHeight: 160
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--df-accent)'; e.currentTarget.style.background = '#fdf5ed' }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--df-line-2)'; e.currentTarget.style.background = 'transparent' }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 9,
                background: 'var(--df-success-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--df-primary)'
              }}>
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--df-ink-2)' }}>Create New Scenario</div>
                <div style={{ fontSize: 11, color: 'var(--df-ink-3)', marginTop: 2 }}>Clone from live routing or an existing draft</div>
              </div>
            </button>
          )}

          {filtered.length === 0 && filter !== 'all' && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '48px 0', color: 'var(--df-ink-3)' }}>
              No {filter} scenarios found
            </div>
          )}
        </div>
      )}

      <CreateScenarioModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        scenarios={scenarios}
        creating={creating}
        onSubmit={createScenario}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  )
}

function CreateScenarioModal ({ open, onClose, scenarios, creating, onSubmit }) {
  const [name, setName] = useState('')
  const [horizonDays, setHorizonDays] = useState(30)
  const [seedMode, setSeedMode] = useState('live')

  const handleSubmit = () => {
    onSubmit({
      name: name.trim() || undefined,
      horizonDays,
      sourceScenarioId: seedMode !== 'live' && seedMode !== 'locked_only' ? seedMode : undefined,
      seedMode: seedMode === 'locked_only' ? 'locked_only' : undefined
    })
  }

  const handleEnter = () => { setName(''); setHorizonDays(30); setSeedMode('live') }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth onEnter={handleEnter}>
      <DialogTitle style={{ borderBottom: '1px solid var(--df-line)', paddingBottom: 12 }}>
        Create New Scenario
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" style={{ gap: 16, paddingTop: 16 }}>
          <TextField label="Scenario Name" variant="outlined" size="small" fullWidth value={name}
            onChange={e => setName(e.target.value)} placeholder="e.g. Spring 2026 Rebalance" autoFocus />
          <TextField label="Horizon Days" variant="outlined" size="small" type="number" fullWidth value={horizonDays}
            onChange={e => setHorizonDays(parseInt(e.target.value, 10) || 30)} inputProps={{ min: 7, max: 365 }} />
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Starting Point</InputLabel>
            <Select value={seedMode} onChange={e => setSeedMode(e.target.value)} label="Starting Point">
              <MenuItem value="live">Live assignments (keep all current)</MenuItem>
              <MenuItem value="locked_only">Locked only (clear non-locked)</MenuItem>
              {(scenarios || []).map(s => (
                <MenuItem key={s._id} value={s._id}>{s.name} ({s.status})</MenuItem>
              ))}
            </Select>
          </FormControl>
          {seedMode === 'locked_only' && (
            <Typography variant="caption" style={{ color: 'var(--df-accent)' }}>
              All non-locked properties will start unassigned. Only locked crew assignments are kept.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid var(--df-line)', padding: '12px 24px' }}>
        <Button onClick={onClose} style={{ color: 'var(--df-ink-2)' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={creating}
          style={{ background: 'var(--df-primary)', color: '#fff' }}>
          {creating ? 'Creating…' : 'Create Draft'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const mapStateToProps = (state) => ({ url: state.url })
export default connect(mapStateToProps)(RoutingScenarioDashboard)
